import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogIn, Mail } from "lucide-react";
import { useAuth } from "../lib/auth";
import { getGoogleAuthConfig, renderGoogleLoginButton } from "../lib/google-identity";
import { InlineNotice } from "../components/ui";

export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const googleButtonRef = useRef<HTMLDivElement | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [googleClientId, setGoogleClientId] = useState("");
  const [googleConfigLoaded, setGoogleConfigLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    void getGoogleAuthConfig()
      .then((config) => {
        if (!cancelled && config.enabled && config.clientId) {
          setGoogleClientId(config.clientId);
        }
      })
      .catch(() => {
        // Email/password remains available even if Google configuration cannot be loaded.
      })
      .finally(() => {
        if (!cancelled) setGoogleConfigLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!googleClientId || !googleButtonRef.current) {
      return;
    }

    let cancelled = false;

    void renderGoogleLoginButton(
      googleButtonRef.current,
      googleClientId,
      (credential) => {
        if (cancelled) return;

        void (async () => {
          setBusy(true);
          setError("");
          try {
            await auth.loginWithGoogle(credential);
            navigate("/");
          } catch (err) {
            setError(err instanceof Error ? err.message : "Không thể đăng nhập bằng Google");
          } finally {
            setBusy(false);
          }
        })();
      },
    ).catch((err) => {
      if (!cancelled) {
        setError(err instanceof Error ? err.message : "Không thể tải Google Sign-In");
      }
    });

    return () => {
      cancelled = true;
    };
  }, [auth, navigate, googleClientId]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await auth.login(email, password);
      navigate("/");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Không thể đăng nhập");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-brand">
          <img src="/logo.png" alt="BLedger" />
          <div>
            <h1>Sổ Kinh Doanh</h1>
            <p>Đơn giản để bán hàng. Rõ ràng để quản lý.</p>
          </div>
        </div>

        {error ? <InlineNotice tone="danger" title={error} /> : null}

        <form onSubmit={submit} className="form-stack">
          <label>
            Email
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </label>
          <label>
            Mật khẩu
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </label>
          <button className="btn btn-primary btn-lg" disabled={busy}>
            <LogIn size={20} /> {busy ? "Đang đăng nhập..." : "Đăng nhập bằng Email"}
          </button>
        </form>

        {googleClientId ? (
          <>
            <div
              aria-hidden="true"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                margin: "1rem 0",
                color: "var(--color-muted, #6b7280)",
                fontSize: "0.82rem",
              }}
            >
              <span style={{ height: 1, flex: 1, background: "var(--color-border, #e5e7eb)" }} />
              <span>hoặc</span>
              <span style={{ height: 1, flex: 1, background: "var(--color-border, #e5e7eb)" }} />
            </div>

            <div
              ref={googleButtonRef}
              aria-label="Đăng nhập bằng Google"
              style={{
                minHeight: 44,
                width: "100%",
                display: "flex",
                justifyContent: "center",
                opacity: busy ? 0.6 : 1,
                pointerEvents: busy ? "none" : "auto",
              }}
            />
          </>
        ) : null}

        <div
          className="auth-links"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem",
            marginTop: "1.25rem",
          }}
        >
          <div>
            <span>Chưa có tài khoản? </span>
            <Link to="/signup" style={{ fontWeight: 700 }}>
              <Mail size={15} style={{ verticalAlign: "text-bottom", marginRight: 4 }} />
              Tạo tài khoản bằng Email
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
